import { create } from "zustand";
import { createJSONStorage, persist, StateStorage } from "zustand/middleware";
import { Preferences } from "@capacitor/preferences";
import { Capacitor } from "@capacitor/core";
import * as Types from "@/models/types";
import * as serverClient from "@/models/serverClient";
import { useAuth } from "@/models/useAuth";
import { useUISlice } from "./useUISlice";
import { i18n } from "@/locales";
import { Clipboard } from "@capacitor/clipboard";
import removeMd from "remove-markdown";
import toast from "react-hot-toast";
import {
  downloadFile,
  downloadMarkdownAsDocx,
  getBasePath,
  getNoteId,
} from "./utils";
import { useRouter } from "@/_router";
import { logFunnelEvent } from "@/fetchData/datadog";

let _toastId: string | undefined;
const storage: StateStorage = {
  getItem: async (name: string): Promise<string | null> => {
    const { value } = await Preferences.get({ key: name });
    return value;
  },
  setItem: async (name: string, value: string): Promise<void> => {
    await Preferences.set({ key: name, value });
  },
  removeItem: async (name: string): Promise<void> => {
    await Preferences.remove({ key: name });
  },
};

type State = {
  _hasHydrated: boolean;
  userToken?: string;
  isSubscribed: boolean;
  userData?: Partial<Types.User>;
  notes: Types.Note[];
  websocketToken?: {
    value: string;
    expiresAt: number;
  };
};

type Actions = {
  setUserToken: (token: string) => void;
  setHasHydrated: (status: boolean) => void;
  setIsSubscribed: (isSubscribed: boolean) => void;
  subscription: () => {
    status: () => Promise<void>;
    stripeCheckout: (plan: Types.SubscriptionPlan) => Promise<void>;
    onStripeCheckoutSuccess: (params: {
      router: ReturnType<typeof useRouter>;
    }) => Promise<void>;
    stripeBillingPortal: () => Promise<void>;
  };
  user: () => {
    get: () => Promise<void>;
    saveTemplate: (params: {
      template: Types.User["listTemplates"][number];
    }) => Promise<void>;
    deleteTemplate: (
      templateId: string,
      callback?: () => void
    ) => Promise<void>;
    update: (params: {
      updates: Pick<Types.User, "preferences">;
    }) => Promise<void>;
    profile: (params: {
      onNoteListCallback?: (nextToken: string) => void;
    }) => Promise<void>;
  };
  note: () => {
    create: (params: {
      partialNote: Omit<
        Types.Note,
        "createdAt" | "updatedAt" | "id" | "userId"
      >;
      callback?: (note: Types.Note) => void;
      onError?: (error: "trial_ended" | string) => void;
    }) => Promise<void>;
    getWebsocketToken: () => Promise<string>;
    update: (params: {
      noteId: string;
      updates: Partial<Types.Note>;
      callback?: (note: Types.Note) => void;
      disableOptimisticUpdate?: boolean;
    }) => Promise<void>;
    transcribe: (params: {
      noteId: string;
      type: Parameters<typeof serverClient.transcribeAsync>[0]["type"];
    }) => Promise<void>;
    rename: (params: { noteId: string }) => Promise<void>;
    export: (params: {
      noteId: string;
      type: "pdf" | "text" | "markdown" | "docx";
    }) => Promise<void>;
    copy: (params: { noteId: string }) => Promise<void>;
    email: (params: { noteId: string }) => Promise<void>;
    delete: (params: {
      noteId: string;
      router: ReturnType<typeof useRouter>;
    }) => Promise<void>;
    editWithAI: (params: {
      inputText: string;
      editOptions?: Types.AIEditOption[];
    }) => Promise<string | undefined>;
    list: (params: {
      nextToken?: string;
      callback?: (nextToken: string) => void;
    }) => Promise<void>;
  };

  reset: () => void;
};

export const initialState: State = {
  _hasHydrated: false,
  isSubscribed: false,
  notes: [],
};

// create store
export const useUserSlice = create<State & Actions>()(
  persist(
    (set, get) => ({
      ...initialState,
      setHasHydrated: (status) => {
        set({ _hasHydrated: status });
      },
      setUserToken: (token) => {
        set({ userToken: token });
      },
      setIsSubscribed: (isSubscribed) => {
        set({ isSubscribed });
      },
      user: () => ({
        get: async () => {
          if (!useAuth.isAuthenticated()) return;

          const user = await serverClient.userAsync({ type: "get" });
          if (!user?.data?.user) return;

          set({ userData: user.data.user });
        },
        saveTemplate: async ({ template }) => {
          if (!useAuth.isAuthenticated()) return;

          const previousTemplates = get().userData?.listTemplates || [];
          const existingTemplateIndex = previousTemplates.findIndex(
            (t) => t.id === template.id
          );

          if (existingTemplateIndex !== -1) {
            previousTemplates[existingTemplateIndex] = template;
          } else {
            previousTemplates.push(template);
          }

          set({
            userData: { ...get().userData, listTemplates: previousTemplates },
          });

          await serverClient.userAsync({
            type: "update",
            payload: { updates: { listTemplates: previousTemplates } },
          });

          const userRes = await serverClient.userAsync({ type: "get" });
          if (!userRes?.data?.user) return;

          set({ userData: userRes.data.user });
          toast.success(i18n.t("useUserSlice.saveEditSuccess"));
        },
        deleteTemplate: async (templateId, callback) => {
          if (!useAuth.isAuthenticated()) return;

          const handleDelete = async () => {
            const previousTemplates = get().userData?.listTemplates || [];
            const newTemplates = previousTemplates.filter(
              (t) => t.id !== templateId
            );

            set({
              userData: { ...get().userData, listTemplates: newTemplates },
            });

            await serverClient.userAsync({
              type: "update",
              payload: { updates: { listTemplates: newTemplates } },
            });

            const userRes = await serverClient.userAsync({ type: "get" });
            if (!userRes?.data?.user) return;

            set({ userData: userRes.data.user });
            toast.success(i18n.t("useUserSlice.deleteEditSuccess"));
          };

          useUISlice.getState().setAlertModal({
            title: i18n.t("useUserSlice.deleteEditTitle"),
            subtitle: i18n.t("useUserSlice.deleteEditSubtitle"),
            cancelText: i18n.t("useUserSlice.cancelButton"),
            onDismiss: () => {
              useUISlice.getState().setAlertModal(undefined);
            },
            button: {
              text: i18n.t("useUserSlice.deleteEditButton"),
              destructive: true,
              onPress: () => {
                handleDelete();
                useUISlice.getState().setAlertModal(undefined);
              },
            },
          });

          if (callback) {
            callback();
          }
        },
        update: async ({ updates }) => {
          if (!useAuth.isAuthenticated()) return;

          set({
            userData: { ...get().userData, ...updates },
          });

          const res = await serverClient.userAsync({
            type: "update",
            payload: { updates },
          });

          if (!res?.data?.user) return;

          set({
            userData: res.data.user,
          });
        },
        profile: async ({ onNoteListCallback }) => {
          if (!useAuth.isAuthenticated()) return;
          await Promise.all([
            // get()
            //   .note()
            //   .list({ nextToken: undefined, callback: onNoteListCallback }),
            get().user().get(),
            get().subscription().status(),
          ]);
        },
      }),
      note: () => ({
        create: async ({ partialNote, callback, onError }) => {
          if (!useAuth.isAuthenticated()) return;

          const res = await serverClient.noteAsync({
            type: "create",
            payload: partialNote,
          });

          if (res?.error === "trial_ended") {
            useUISlice.getState().setPaywallVisible(true);
            toast.dismiss(_toastId);
            onError?.("trial_ended");
            return;
          }

          if (!res?.data?.note) return;

          set({ notes: [res.data.note, ...get().notes] });

          if (callback) {
            callback(res.data.note);
          }
        },
        getWebsocketToken: async () => {
          if (!useAuth.isAuthenticated()) return;

          const currentToken = get().websocketToken;
          if ((currentToken?.expiresAt || 0) > Date.now()) {
            return currentToken?.value;
          }

          const res = await serverClient.transcribeAsync({
            type: "GetWebsocketToken",
          });

          if (res?.error && res.error === "trial_ended") {
            useUISlice.getState().setPaywallVisible(true);
            return;
          }

          if (!res?.data?.token) return;

          set({
            websocketToken: {
              value: res.data.token,
              expiresAt: res.data.tokenExpiresAt,
            },
          });
          return res.data.token;
        },
        transcribe: async ({ noteId, type }) => {
          if (!useAuth.isAuthenticated()) return;

          const transcribeRes = await serverClient.transcribeAsync({
            noteId,
            type,
          });

          if (!transcribeRes?.data?.note) {
            return;
          }

          get().note().update({ noteId, updates: transcribeRes.data.note });
        },
        update: async ({
          noteId,
          updates,
          callback,
          disableOptimisticUpdate,
        }) => {
          if (!useAuth.isAuthenticated()) return;

          if (!get().notes.find((note) => note.id === noteId)) return;

          if (!disableOptimisticUpdate) {
            set({
              notes: get().notes.map((note) =>
                note.id === noteId ? { ...note, ...updates } : note
              ),
            });
          }

          const res = await serverClient.noteAsync({
            type: "update",
            payload: { id: noteId, updates },
          });

          if (res?.data?.note) {
            if (callback) {
              callback(res.data.note);
            }
            set({
              notes: get().notes.map((note) =>
                note.id === noteId ? res.data.note : note
              ),
            });
          }
        },
        list: async ({ nextToken, callback }) => {
          if (!useAuth.isAuthenticated()) return;

          const notes = await serverClient.noteAsync({
            type: "list",
            payload: { nextToken },
          });
          if (!notes?.data?.notes) return;

          set({
            notes: nextToken
              ? [...get().notes, ...notes.data.notes]
              : notes.data.notes,
          });

          if (callback) {
            callback(notes.data.nextToken);
          }
        },
        delete: async ({ noteId, router }) => {
          if (!useAuth.isAuthenticated()) return;

          useUISlice.getState().setAlertModal({
            title: i18n.t("useUserSlice.deleteDocumentTitle"),
            subtitle: i18n.t("useUserSlice.deleteDocumentSubtitle"),
            cancelText: i18n.t("useUserSlice.cancelButton"),
            button: {
              text: i18n.t("useUserSlice.deleteDocumentDeleteButton"),
              destructive: true,
              onPress: async () => {
                const targetIndex = get().notes.findIndex(
                  (note) => note.id === noteId
                );
                const nextNoteId = get().notes[targetIndex + 1]?.id;

                set({
                  notes: get().notes.filter((note) => note.id !== noteId),
                });

                useUISlice.getState().setAlertModal(undefined);
                const isCurrentlySelected = getNoteId() === noteId;

                if (isCurrentlySelected) {
                  if (nextNoteId) {
                    router.replace(`/?noteId=${nextNoteId}`);
                  } else {
                    router.replace("/");
                  }
                }

                await serverClient.noteAsync({
                  type: "delete",
                  payload: { id: noteId },
                });
                toast.success(i18n.t("useUserSlice.deleteDocumentSuccess"));
              },
            },
          });
        },
        rename: async ({ noteId }) => {
          if (!useAuth.isAuthenticated()) return;

          useUISlice.getState().setAlertPrompt({
            title: i18n.t("useUserSlice.renameDocumentTitle"),
            defaultValue: get().notes.find((note) => note.id === noteId)?.name,
            placeholder: i18n.t("useUserSlice.renameDocumentSubtitle"),
            button: {
              text: i18n.t("useUserSlice.renameDocumentRenameButton"),
              onPress: (text: string) => {
                if (text.trim().length === 0) return;

                useUserSlice
                  .getState()
                  .note()
                  .update({ noteId, updates: { name: text.trim() } });
                toast.success(i18n.t("useUserSlice.renameDocumentSuccess"));
                useUISlice.getState().setAlertPrompt(undefined);
              },
            },
          });
        },
        editWithAI: async ({ inputText, editOptions }) => {
          if (!useAuth.isAuthenticated()) return;

          if (inputText.trim().length === 0) return;

          const res = await serverClient.noteAsync({
            type: "editWithAI",
            payload: { inputText, editOptions },
          });

          if (!res?.data?.outputText) return;

          return res.data.outputText;
        },
        copy: async ({ noteId }) => {
          if (!useAuth.isAuthenticated()) return;

          const note = get().notes.find((note) => note.id === noteId);
          if (!note) return;

          const text = removeMd(note.markdown);

          logFunnelEvent("[4.1] - Copy");

          Clipboard.write({ string: text }).then(() => {
            toast.success(i18n.t("useUserSlice.copied"));
          });
        },
        email: async ({ noteId }) => {
          if (!useAuth.isAuthenticated()) return;

          const note = get().notes.find((note) => note.id === noteId);
          if (!note) return;

          const text = removeMd(note.markdown);

          window.open(
            `mailto:${get().userData?.email}?subject=${encodeURIComponent(note.name)}&body=${encodeURIComponent(text)}`,
            "_self"
          );
        },
        export: async ({ noteId, type }) => {
          if (!useAuth.isAuthenticated()) return;

          const note = get().notes.find((note) => note.id === noteId);
          if (!note) return;

          const markdown = note.markdown;

          logFunnelEvent("[4.2] - Downloaded As Docx");

          switch (type) {
            case "text":
              downloadFile({
                type: "txt",
                text: removeMd(markdown),
                fileName: `${note.name}.txt`,
              });
              break;

            case "markdown":
              downloadFile({
                type: "txt",
                text: markdown,
                fileName: `${note.name}.md`,
              });
              break;

            case "docx":
              await downloadMarkdownAsDocx({
                markdown,
                fileName: `${note.name}`,
              });
              break;
          }
        },
      }),
      subscription: () => ({
        status: async () => {
          if (!useAuth.isAuthenticated()) return;

          const res = await serverClient.subscriptionAsync({ type: "status" });
          if (!res?.data?.subscription) return;
          set({ isSubscribed: res?.data?.subscription?.isActive });
        },
        stripeCheckout: async (plan) => {
          if (!useAuth.isAuthenticated()) return;

          const res = await serverClient.subscriptionAsync({
            type: "stripeCheckout",
            payload: { baseAppUrl: window.location.href, plan },
          });
          if (!res?.data?.url) return;

          logFunnelEvent("[5] - stripe checkout");

          window.open(res?.data?.url, "_self");
        },
        stripeBillingPortal: async () => {
          if (!useAuth.isAuthenticated()) return;

          const response = await serverClient.subscriptionAsync({
            type: "stripeBillingPortal",
            payload: { baseAppUrl: window.location.href },
          });

          if (!response?.data?.url) return;

          window.open(response?.data?.url, "_self");
        },
        onStripeCheckoutSuccess: async (params) => {
          if (!useAuth.isAuthenticated()) return;
          const slug = params.router.query.checkout_success;
          if (!slug || slug !== "true") return;

          const fbq = await import("@/fetchData/fpixel");
          const gtag = await import("@/fetchData/gtag");

          const planPrices: Record<Types.SubscriptionPlan, number> = {
            YEARLY_MID_TIER: 99.99,
            MONTHLY_MID_TIER: 19.99,
          };
          const value =
            planPrices[params.router.query.plan as Types.SubscriptionPlan] ||
            19.99;

          fbq.event("Subscribe", { currency: "USD", value: value.toString() });
          gtag.conversion({ value, transactionId: String(Date.now()) });
          get().subscription().status();

          logFunnelEvent("[6] - stripe checkout success");

          useUISlice.getState().setAlertModal({
            title: i18n.t("useUserSlice.subscriptionSuccess"),
            subtitle: i18n.t("useUserSlice.subscriptionSuccessDescription"),
            hideDismissIcon: true,
            shouldPersistBackdropClick: true,
            button: {
              text: i18n.t("useUserSlice.subscriptionSuccessButton"),
              onPress: async () => {
                // Wait for 2 seconds to ensure backend has processed the subscription
                useUISlice.getState().setAlertModal({
                  ...useUISlice.getState().alertModal,
                  loading: true,
                });

                await new Promise((resolve) => setTimeout(resolve, 2000));
                useUISlice.getState().setAlertModal(undefined);
                const url = new URL(window.location.href);
                url.searchParams.delete("checkout_success");
                url.searchParams.delete("plan");
                params.router.replace(url.pathname + url.search);
              },
            },
          });
        },
      }),

      reset: () => {
        set({
          userToken: undefined,
          userData: undefined,
          isSubscribed: false,
          notes: [],
          user: undefined,
        });
      },
    }),
    {
      name: `ezwording-${Capacitor.getPlatform()}-user-storage`,
      storage: createJSONStorage(() => storage),
      onRehydrateStorage: () => (state) => {
        state && state.setHasHydrated(true);
      },
    }
  )
);
